import React, { FC, useMemo } from 'react'
import { IonInput, IonItem, IonLabel, IonNote, IonIcon, IonButton } from '@ionic/react'
import { checkmarkCircle, alertCircle, copyOutline } from 'ionicons/icons'
import './ShebaInput.scss'
export interface ShebaInputProps {
  value: string
  onChange: (value: string) => void
  onVerify?: (sheba: string) => void
  label?: string
  placeholder?: string
  disabled?: boolean
  loading?: boolean
  verified?: boolean
  error?: string
  showValidation?: boolean
  showCopy?: boolean
  required?: boolean
}

export const toEnglishDigits = (value: string): string => {
  return value
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
}

export const normalizeSheba = (value: string = ''): string => {
  let result = toEnglishDigits(value).toUpperCase().replace(/[\s-]/g, '')
  // Remove common invisible characters
  result = result.replace(/[\u200B-\u200D\uFEFF]/g, '')
  // Iranian IBAN without IR
  if (/^\d{24}$/.test(result)) {
    result = 'IR' + result
  }
  return result
}

export const formatSheba = (value: string): string => {
  const normalized = normalizeSheba(value)
  return normalized.match(/.{1,4}/g)?.join(' ') ?? ''
}

export const isValidSheba = (value: string): boolean => {
  const sheba = normalizeSheba(value)
  // IR + 24 digits
  if (!/^IR\d{24}$/.test(sheba)) {
    return false
  }

  // Move first 4 characters to the end
  const rearranged = sheba.substring(4) + sheba.substring(0, 4)
  let remainder = 0
  for (const char of rearranged) {
    if (char >= '0' && char <= '9') {
      remainder = (remainder * 10 + Number(char)) % 97
    } else {
      // A = 10, B = 11, ... Z = 35
      const number = char.charCodeAt(0) - 55
      const firstDigit = Math.floor(number / 10)
      const secondDigit = number % 10
      remainder = (remainder * 10 + firstDigit) % 97
      remainder = (remainder * 10 + secondDigit) % 97
    }
  }
  return remainder === 1
}

const ShebaInput: FC<ShebaInputProps> = ({
  value,
  onChange,
  onVerify,
  label = 'شبای مقصد',
  placeholder = 'IR00 0000 0000 0000 0000 0000 00',
  disabled = false,
  loading = false,
  verified = false,
  error,
  showValidation = true,
  showCopy = true,
  required = false,
}) => {
  // Normalize input for validation only.
  // Never modify input while typing.

  const normalized = useMemo(() => normalizeSheba(value), [value])
  const complete = normalized.length === 26
  const valid = useMemo(() => isValidSheba(normalized), [normalized])
  const handleChange = (input: string) => {
    // Keep raw input to prevent cursor jumping
    onChange(input)
  }

  const handleVerify = () => {
    if (!valid || loading || disabled || !onVerify) {
      return
    }
    onVerify(normalized)
  }

  const handleCopy = async () => {
    if (!normalized) {
      return
    }

    try {
      await navigator.clipboard.writeText(normalized)
    } catch (error) {
      console.error('Copy Sheba error:', error)
    }
  }

  return (
    <div className='sheba-input'>
      <IonItem className='sheba-item' lines='full'>
        <IonLabel position='stacked'>
          {label}
          {required && <span className='sheba-required'>*</span>}
        </IonLabel>
        <IonInput
          value={value}
          disabled={disabled || loading}
          onIonInput={(event) => {
            const input = event.target as HTMLIonInputElement
            handleChange(String(input.value ?? ''))
          }}
          placeholder={placeholder}
          inputmode='text'
          autocapitalize='characters'
          autocomplete='off'
          spellcheck={false}
          dir='ltr'
          maxlength={26}
          className='sheba-field'
        />
        {showCopy && normalized.length > 0 && (
          <IonButton
            slot='end'
            fill='clear'
            size='small'
            type='button'
            disabled={disabled || loading}
            onClick={handleCopy}>
            <IonIcon icon={copyOutline} slot='icon-only' />
          </IonButton>
        )}
      </IonItem>
      {showValidation && (
        <div className='sheba-status'>
          {error ? (
            <IonNote color='danger'>
              <IonIcon icon={alertCircle} />
              {error}
            </IonNote>
          ) : verified ? (
            <IonNote color='success'>
              <IonIcon icon={checkmarkCircle} />
              استعلام حساب با موفقیت انجام شد.
            </IonNote>
          ) : complete && valid ? (
            <IonNote color='success'>
              <IonIcon icon={checkmarkCircle} />
              شماره شبا از نظر ساختاری معتبر است.
            </IonNote>
          ) : complete && !valid ? (
            <IonNote color='danger'>
              <IonIcon icon={alertCircle} />
              شماره شبای وارد شده معتبر نیست.
            </IonNote>
          ) : normalized.length > 0 ? (
            <IonNote color='medium'>{26 - normalized.length} کاراکتر باقی مانده است.</IonNote>
          ) : null}
        </div>
      )}
      {onVerify && (
        <IonButton
          type='button'
          size='small'
          expand='block'
          className='sheba-verify-button'
          disabled={!valid || disabled || loading || verified}
          onClick={handleVerify}>
          {loading ? 'در حال استعلام...' : verified ? 'شبا تأیید شده است' : 'استعلام شبا'}
        </IonButton>
      )}
    </div>
  )
}

export default ShebaInput
